import React, { Component } from 'react'
import { AiOutlineReload } from "react-icons/ai";
import { BsBoxSeam } from "react-icons/bs";
import { getInformationWithDataV3 } from '../../services/CABE';
import { getValueCookie } from '../../services/cookieService';
import "../../css/general-style.css"
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu";

/**
 * "My Orders Status" — new page, part of the 2026-08 SO reliability initiative.
 *
 * Why this exists: before this, a user submitting a Sales Order had NO visibility
 * into what happened after they hit submit — if local-queue-worker was slow, stuck,
 * or failed, the only signal was silence, which users reasonably read as "it's
 * broken" and complained. This shows every SO they've submitted and its real
 * status in plain language, auto-refreshing while anything is still in flight.
 *
 * Reads queue_log (v3 endpoint /v3/queue/my-status, filtered by requestedBy) —
 * the exact same table local-queue-worker writes to, so this is always the real
 * status, not a guess.
 */
export default class MyOrdersStatus extends Component {
    constructor(props) {
        super(props)
        this._refreshTimer = null
    }

    state = {
        loading: false,
        loaded: false,
        errorMessage: '',
        orders: [],
    }

    componentDidMount() {
        this.fetchOrders()
        // Poll every 8s while anything is still in flight — stops itself once
        // everything shown is done/error/dead (see fetchOrders' setState callback).
        this._refreshTimer = setInterval(() => this.fetchOrders(false), 8000)
    }

    componentWillUnmount() {
        if (this._refreshTimer) clearInterval(this._refreshTimer)
    }

    async fetchOrders(showLoading) {
        const shouldShowLoading = typeof showLoading === 'undefined' ? true : showLoading
        const userName = getValueCookie('userName')
        if (!userName) return

        if (shouldShowLoading) this.setState({ loading: true, errorMessage: '' })

        try {
            const resp = await getInformationWithDataV3('/v3/queue/my-status', { userName, limit: 50 })
            if (!resp || !resp.status || resp.status.code !== 1) {
                this.setState({ loading: false, loaded: true, errorMessage: 'No se pudo cargar tu lista de órdenes.' })
                return
            }
            const orders = Array.isArray(resp.data) ? resp.data : []
            this.setState({ loading: false, loaded: true, orders, errorMessage: '' })
        } catch (error) {
            this.setState({ loading: false, loaded: true, errorMessage: error.message || 'Error al cargar tus órdenes.' })
        }
    }

    // Plain-language status — this is the whole point of the page: no jargon,
    // no guessing, tell the user exactly where their order is right now.
    statusInfo(order) {
        const st = String(order.status || '').toLowerCase()
        if (st === 'pending') return { label: 'En cola, esperando procesarse', color: '#D97706', bg: 'rgba(245,158,11,0.08)', border: '#FDE68A' }
        if (st === 'claimed' || st === 'processing') return { label: 'Procesando...', color: '#1D4ED8', bg: 'rgba(37,99,235,0.08)', border: '#BFDBFE' }
        if (st === 'done') return { label: '✓ Completada', color: '#059669', bg: 'rgba(16,185,129,0.08)', border: '#A7F3D0' }
        if (st === 'error') return { label: 'Hubo un problema, reintentando automáticamente...', color: '#D97706', bg: 'rgba(245,158,11,0.08)', border: '#FDE68A' }
        if (st === 'dead') return { label: '✕ No se pudo procesar — soporte ya fue notificado', color: '#DC2626', bg: 'rgba(239,68,68,0.08)', border: '#FECACA' }
        return { label: st || 'Desconocido', color: '#64748B', bg: 'rgba(100,116,139,0.08)', border: '#E2E8F0' }
    }

    fmtDateTime(v) {
        if (!v) return '—'
        return String(v).replace('T', ' ').slice(0, 19)
    }

    render() {
        const { orders, loading, loaded, errorMessage } = this.state
        const hasOrders = orders.length > 0

        return (
            <div className="dash-page" style={{ paddingTop: '70px' }}>
                <Menu />

                {/* Hero */}
                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #2563EB 100%)', padding: '1.25rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '12px', padding: '0.75rem', display: 'flex' }}>
                        <BsBoxSeam style={{ color: 'white', fontSize: '1.75rem' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.375rem', margin: 0 }}>My Orders Status</h1>
                        <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.8rem' }}>
                            {hasOrders ? `${orders.length} órdenes recientes` : 'Aquí ves el estado real de tus últimas órdenes enviadas'}
                        </p>
                    </div>
                    <button onClick={() => this.fetchOrders()} disabled={loading}
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', color: 'white', fontWeight: 700, padding: '0.45rem 1rem', fontSize: '0.875rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                        <AiOutlineReload /> {loading ? 'Actualizando...' : 'Actualizar'}
                    </button>
                </div>

                <div style={{ padding: '1.5rem 2rem' }}>
                    {errorMessage && (
                        <div className="metric-card status-danger mb-4" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.5rem', color: '#DC2626', fontWeight: 700 }}>✕</span>
                            <div>
                                <div style={{ fontWeight: 700, color: '#991B1B', fontSize: '0.95rem' }}>Error</div>
                                <div style={{ color: '#DC2626', fontSize: '0.8rem' }}>{errorMessage}</div>
                            </div>
                        </div>
                    )}

                    {!loaded && !loading && !errorMessage && (
                        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#94A3B8' }}>
                            <BsBoxSeam style={{ fontSize: '3rem', opacity: 0.2, marginBottom: '1rem' }} />
                            <p style={{ fontSize: '0.9rem', fontWeight: 500 }}>Cargando tus órdenes...</p>
                        </div>
                    )}

                    {loaded && !hasOrders && !errorMessage && (
                        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#94A3B8' }}>
                            <BsBoxSeam style={{ fontSize: '3rem', opacity: 0.2, marginBottom: '1rem' }} />
                            <p style={{ fontSize: '0.9rem', fontWeight: 500 }}>Aún no has enviado ninguna orden, o no hay registros recientes.</p>
                        </div>
                    )}

                    {hasOrders && (
                        <div className="row g-3">
                            {orders.map((order, i) => {
                                const info = this.statusInfo(order)
                                return (
                                    <div key={order.requestId || i} className="col-12 col-md-6 col-xl-4">
                                        <div style={{ background: '#ffffff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '14px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                                                <span style={{ background: 'rgba(37,99,235,0.08)', color: '#1e40af', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>
                                                    {order.action}
                                                </span>
                                                <span style={{ background: info.bg, color: info.color, border: `1px solid ${info.border}`, borderRadius: '6px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>
                                                    {info.label}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.25rem' }}>
                                                Enviada: {this.fmtDateTime(order.createdAt)}
                                            </div>
                                            {order.errorMessage && String(order.status).toLowerCase() !== 'done' && (
                                                <div style={{ fontSize: '0.75rem', color: '#991B1B', marginTop: '0.4rem', background: 'rgba(239,68,68,0.06)', borderRadius: '6px', padding: '0.4rem 0.6rem' }}>
                                                    {order.errorMessage}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
        )
    }
}
